import React, { useState } from 'react';
import { X, Phone, CheckCircle, AlertCircle, Mail, Send, Upload, FileText, Image, Trash2 } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api-ssrpro.siliconmount.com';

// ---- CLARITY helper ----
const track = (name, props = {}) => {
  if (typeof window !== 'undefined' && window.clarity) {
    window.clarity('event', name, props);
  }
};

// JWT Token helper function (optional for this endpoint)
const getJwtToken = () => {
  return localStorage.getItem('token') ||
    localStorage.getItem('jwt') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('jwtToken') ||
    sessionStorage.getItem('token') ||
    sessionStorage.getItem('jwt') ||
    sessionStorage.getItem('authToken') ||
    sessionStorage.getItem('jwtToken');
};

const ContactModal = ({ isOpen = false, onClose, apiEndpoint = '/submit' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    subject: '',
    message: ''
  });

  const [attachment, setAttachment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Use external isOpen prop or internal state
  const modalIsOpen = isOpen || internalIsOpen;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];

    if (!file) {
      setAttachment(null);
      return;
    }

    // Check file size (2MB limit)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File size must be less than 2MB');
      e.target.value = '';
      return;
    }

    // Check file type (images and PDFs only)
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf'
    ];

    if (!allowedTypes.includes(file.type)) {
      alert('Only image files (JPEG, PNG, GIF, WebP) and PDF files are allowed');
      e.target.value = '';
      return;
    }

    setAttachment(file);

    // ✅ Clarity: attachment added
    track('contact_attachment_added', {
      placement: isOpen ? 'external_modal' : 'floating_button',
      fileType: file.type,
      fileSizeKb: Math.round(file.size / 1024),
    });
  };

  const removeAttachment = () => {
    // ✅ Clarity: attachment removed
    if (attachment) {
      track('contact_attachment_removed', {
        placement: isOpen ? 'external_modal' : 'floating_button',
        fileType: attachment.type,
        fileSizeKb: Math.round(attachment.size / 1024),
      });
    }

    setAttachment(null);
    const fileInput = document.getElementById('attachment');
    if (fileInput) fileInput.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return <Image size={20} className="text-blue-600" />;
    if (file.type === 'application/pdf') return <FileText size={20} className="text-red-600" />;
    return <FileText size={20} className="text-gray-600" />;
  };

  const validateForm = () => {
    const { name, email, mobile, message } = formData;

    if (!name.trim()) {
      alert('Please enter your name');
      return false;
    }

    if (!email.trim()) {
      alert('Please enter your email');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert('Please enter a valid email address');
      return false;
    }

    if (!mobile.trim()) {
      alert('Please enter your phone number');
      return false;
    }

    // Phone validation: 10 digits, optionally starting with +91 or 91
    const phoneRegex = /^(\+91|91)?[6-9]\d{9}$/;
    if (!phoneRegex.test(mobile.replace(/[\s-]/g, ''))) {
      alert('Please enter a valid 10-digit Indian phone number');
      return false;
    }

    if (!message.trim()) {
      alert('Please enter your message');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      track('contact_submit_validation_failed', {
        placement: isOpen ? 'external_modal' : 'floating_button'
      });
      return;
    }

    // ✅ Clarity: submit clicked
    track('contact_submit_click', {
      placement: isOpen ? 'external_modal' : 'floating_button',
      hasAttachment: !!attachment,
    });

    setIsSubmitting(true);
    setSubmitStatus(null);
    setApiResponse(null);

    try {
      const formDataToSend = new FormData();
      if (attachment) formDataToSend.append('attachment', attachment);

      const queryParams = new URLSearchParams({
        name: formData.name.trim(),
        email: formData.email.trim(),
        mobile: formData.mobile.trim(),
        subject: formData.subject.trim() || 'Contact Form Submission',
        message: formData.message.trim()
      });

      let fullApiUrl;
      if (apiEndpoint.startsWith('http')) {
        fullApiUrl = `${apiEndpoint}?${queryParams.toString()}`;
      } else {
        const cleanEndpoint = apiEndpoint.startsWith('/') ? apiEndpoint : `/${apiEndpoint}`;
        fullApiUrl = `${API_BASE_URL}${cleanEndpoint}?${queryParams.toString()}`;
      }

      const headers = { 'Accept': 'application/json, text/plain, */*' };

      const jwtToken = getJwtToken();
      if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;

      const response = await fetch(fullApiUrl, {
        method: 'POST',
        headers,
        body: formDataToSend,
        mode: 'cors'
      });

      if (response.status === 401) throw new Error('Authentication failed. Please login again.');
      if (response.status === 403) throw new Error('Access forbidden. You don\'t have permission to perform this action.');

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorText = await response.text();
          if (errorText) errorMessage += ` - ${errorText}`;
        } catch (e) {}
        throw new Error(errorMessage);
      }

      let result;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        result = await response.json();
      } else {
        const textResponse = await response.text();
        result = {
          success: true,
          message: textResponse || 'Form submitted successfully!',
          timestamp: new Date().toISOString()
        };
      }

      if (result.success !== false) {
        setApiResponse({
          ...result,
          message: result.message || 'Thank you for contacting myBOQ. Your request has been submitted successfully.',
          timestamp: result.timestamp || new Date().toISOString()
        });
        setSubmitStatus('success');

        // ✅ Clarity: submit success
        track('contact_submit_success', {
          placement: isOpen ? 'external_modal' : 'floating_button',
          hasAttachment: !!attachment,
          hasTicketId: !!result.ticketId
        });

        setFormData({ name: '', email: '', mobile: '', subject: '', message: '' });
        setAttachment(null);

        const fileInput = document.getElementById('attachment');
        if (fileInput) fileInput.value = '';

        // Auto-close modal after 5 seconds
        setTimeout(() => {
          closeModal('auto_close_after_success');
        }, 5000);

      } else {
        throw new Error(result.message || 'Submission failed');
      }

    } catch (error) {
      let errorMessage = error.message;
      let errorAction = null;

      if (error.message.includes('Authentication') || error.message.includes('401')) {
        errorAction = 'LOGIN_REQUIRED';
        errorMessage = 'Authentication may be required. Please try logging in first.';
      } else if (error.message.includes('CORS') || error.message.includes('Access to fetch')) {
        errorAction = 'CORS_ERROR';
        errorMessage = 'Network configuration issue. The server may need to update CORS settings.';
      } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        errorAction = 'NETWORK_ERROR';
        errorMessage = 'Network connection failed. Please check your internet connection and try again.';
      } else if (error.message.includes('403') || error.message.includes('forbidden')) {
        errorAction = 'PERMISSION_DENIED';
        errorMessage = 'You don\'t have permission to submit this form.';
      } else if (error.message.includes('500')) {
        errorAction = 'SERVER_ERROR';
        errorMessage = 'Server error occurred. Please try again later.';
      }

      setSubmitStatus('error');
      setApiResponse({
        message: errorMessage,
        action: errorAction,
        originalError: error.message,
        timestamp: new Date().toISOString()
      });

      // ✅ Clarity: submit error
      track('contact_submit_error', {
        placement: isOpen ? 'external_modal' : 'floating_button',
        action: errorAction || 'UNKNOWN',
        originalError: (error.message || '').slice(0, 200),
        hasAttachment: !!attachment
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = (method = 'unknown') => {
    // ✅ Clarity: modal close
    track('contact_modal_close', {
      placement: isOpen ? 'external_modal' : 'floating_button',
      method,
      submitStatus: submitStatus || 'none'
    });

    setSubmitStatus(null);
    setApiResponse(null);

    if (onClose) onClose();
    setInternalIsOpen(false);
  };

  const openModal = () => {
    setInternalIsOpen(true);

    // ✅ Clarity: modal open (floating button)
    track('contact_modal_open', {
      placement: 'floating_button'
    });
  };

  // ✅ Clarity: if modal is opened via parent prop (e.g., Contact for Demo button)
  // We can’t reliably detect "first time open" without useEffect; keeping it simple and explicit:
  // Prefer tracking in the button that triggers onClose/openModal in App.js.
  // (Optional: you can add a useEffect in this component if you want.)

  return (
    <div className="relative">
      {/* Floating Contact Button */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-40">
          <button
            onClick={openModal}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 group"
            title="Contact Support"
          >
            <div className="relative">
              <Phone size={24} className="group-hover:animate-pulse" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            </div>
          </button>
        </div>
      )}

      {/* Modal Overlay */}
      {modalIsOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn"
          onMouseDown={(e) => {
            // click outside closes
            if (e.target === e.currentTarget) closeModal('overlay_click');
          }}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg h-auto max-h-[95vh] flex flex-col transform transition-all duration-300 animate-slideUp">
            {/* Modal Header */}
            <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-xl flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Contact myBOQ Support/Demo</h2>
                    <p className="text-blue-100 text-sm">We're here to help you!</p>
                  </div>
                </div>
                <button
                  onClick={() => closeModal('close_button')}
                  className="text-white hover:text-blue-200 transition-colors p-2 hover:bg-white hover:bg-opacity-20 rounded-full"
                  aria-label="Close modal"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Modal Body - Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {submitStatus === 'success' ? (
                <div className="text-center py-8 animate-fadeIn">
                  <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="text-green-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-green-600 mb-2">
                    Request Submitted Successfully!
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {apiResponse?.message || 'Thank you for contacting myBOQ. Your request has been submitted successfully.'}
                  </p>
                  {/* ... keep rest of your success UI unchanged ... */}
                </div>
              ) : (
                <div className="space-y-5">
                  {/* --- keep your form UI as-is --- */}
                  {/* (No changes needed below unless you want field-level tracking) */}

                  {/* Form Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="98********"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="your@email.com"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Subject
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Subject of your inquiry"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={2}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                      placeholder="Tell us about your requirements, questions, or how we can help you with BOQ estimations..."
                      required
                    />
                  </div>

                  {/* File Attachment */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Attachment <span className="text-gray-500">(Optional - Max 2MB)</span>
                    </label>

                    {!attachment ? (
                      <div className="relative">
                        <input
                          type="file"
                          id="attachment"
                          accept="image/*,application/pdf"
                          onChange={handleFileUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-blue-400 hover:bg-blue-50 transition-all">
                          <div className="flex items-center justify-center space-x-2">
                            <Upload size={18} className="text-gray-400" />
                            <p className="text-sm text-gray-600">Click to upload</p>
                            <p className="text-xs text-gray-500">(Images/PDF • Max 2MB)</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            {getFileIcon(attachment)}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {attachment.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(attachment.size)}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={removeAttachment}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Remove attachment"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Error Message */}
                  {submitStatus === 'error' && (
                    <div className="flex items-center text-red-600 text-sm bg-red-50 border border-red-200 p-4 rounded-lg">
                      <AlertCircle size={18} className="mr-2 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium">Failed to submit your request</p>
                        <p className="text-xs mt-1">
                          {apiResponse?.message || 'Please try again or contact us directly at hello@myboq.in'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer - Fixed at bottom */}
            {submitStatus !== 'success' && (
              <div className="flex-shrink-0 p-6 pt-0">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none disabled:shadow-none"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      Send to Support Team
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Custom Animations */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        .animate-slideUp { animation: slideUp 0.4s ease-out; }
        .overflow-y-auto::-webkit-scrollbar { width: 6px; }
        .overflow-y-auto::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        .overflow-y-auto::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 10px; }
        .overflow-y-auto::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
      `}</style>
    </div>
  );
};

export default ContactModal;